import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { OrderHistoryDetail } from '../../../../generic/classes/order.history.detail';
import { Logger } from '../../../../generic/logging/default-log.service';
import { ApplicationService } from '../../../../generic/service/application.service';
import { OrderHistoryService } from '../../../../service/order.history.service';
import { firstValueFrom } from 'rxjs';
import { NumberFormatPipe } from '../../../../generic/pipes/number.format.pipe';
import { CustomDatePipe } from '../../../../generic/pipes/custom.date.pipe';
import { RenderProductImageComponent } from '../../../../generic/component/products/render.product.image';
import { HistoryDetailSortComponent } from './history.detail.sort.component';
import { FormsModule } from '@angular/forms';
import { SalespersonTelephoneComponent } from '../../../../generic/component/my/salesperson.telephone.component';

@Component({
    selector: 'order-history-detail',
    styleUrls: [
        '../../../../../assets/style/_order-overview.scss',
        '../../../../../assets/style/_layout.scss',
        'history.detail.component.scss',
    ],
    templateUrl: 'history.detail.component.html',
    standalone: true,
    imports: [
        RouterLink,
        SalespersonTelephoneComponent,
        FormsModule,
        HistoryDetailSortComponent,
        RenderProductImageComponent,
        CustomDatePipe,
        NumberFormatPipe,
    ],
})
export class OrderHistoryDetailComponent implements OnInit {
    historyDetails: Array<OrderHistoryDetail>;

    shipmentDate: Date;
    shipToCode: string;
    shipToName: string;
    orderId: any;
    shipmentDateString: string;
    sortingKey: string = 'inserted';
    sortingDirection: string = 'ASC';

    totalPrice: number = 0;
    totalVolume: number;
    totalVolumeCut: number = 0;
    totalVolumeMisc: number = 0;
    totalVolumePlants: number = 0;

    constructor(
        public orderService: OrderHistoryService,
        public activatedRoute: ActivatedRoute,
        public applicationService: ApplicationService,
        private logger: Logger,
    ) {}

    ngOnInit() {
        this.activatedRoute.params.subscribe(params => {
            this.orderId = params.orderId;
            this.shipmentDateString = params.shipmentDate;
            this.shipToCode = params.deliveryAddressCode;

            this.getOrderHistory(
                this.orderId,
                this.shipToCode,
                this.shipmentDateString,
            );
        });
    }

    getPriceForLine(orderHistoryDetail: OrderHistoryDetail): number {
        return orderHistoryDetail.previousPrice;
    }

    onSortOrderChange(sorting: any) {
        this.sortingKey = sorting.key;
        this.sortingDirection = sorting.direction;

        this.getOrderHistory(
            this.orderId,
            this.shipToCode,
            this.shipmentDateString,
        );
    }

    private getRevenueForLine(orderHistoryDetail: OrderHistoryDetail) {
        return (
            orderHistoryDetail.quantityOrdered *
            orderHistoryDetail.quantityPerBox *
            orderHistoryDetail.previousPrice
        );
    }

    private getOrderHistory(
        orderId: any,
        shipToCode: any,
        shipmentDateString: any,
    ) {
        const getOrderHistoryPromise: Promise<OrderHistoryDetail[]> =
            firstValueFrom(
                this.orderService.getOrderHistoryDetail(
                    orderId,
                    shipToCode,
                    shipmentDateString,
                    this.sortingKey,
                    this.sortingDirection,
                ),
            );

        getOrderHistoryPromise.then(lines => {
            this.historyDetails = lines;

            this.setCalculatedFields();

            if (lines.length > 0) {
                const firstItem: OrderHistoryDetail = lines[0];

                this.shipmentDate = firstItem.shipmentDate;
                this.shipToCode = firstItem.shipToAddress;
                this.shipToName = firstItem.shipToAddressName;
                this.totalVolume = firstItem.shippingTotalVolume;
            }
        });

        getOrderHistoryPromise.catch(error =>
            this.logger.error('Failed to get order history detail', error),
        );
    }

    /**
     * To avoid iterating over the whole set for each calculated field, set them all on load
     * @private
     */
    private setCalculatedFields() {
        if (this.historyDetails) {
            this.historyDetails.forEach(line => {
                this.totalPrice += this.getRevenueForLine(line);
                this.totalVolumeCut += line.volumeCut;
                this.totalVolumeMisc += line.volumeMisc;
                this.totalVolumePlants += line.volumePlants;
            });
        }
    }
}
